import React, { useEffect, useState, useRef } from 'react';
import { ChevronDownIcon, XIcon, CheckIcon } from 'lucide-react';
interface Option {
  value: string;
  label: string;
}
interface MultiSelectProps {
  options: Option[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
}
const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select options...'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const toggleOption = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter(v => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };
  const removeOption = (optionValue: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(value.filter(v => v !== optionValue));
  };
  const selectedLabels = options.filter(option => value.includes(option.value)).map(option => option.label);
  return <div className="relative" ref={dropdownRef} data-id="element-35">
      <div className="flex flex-wrap min-h-[38px] items-center w-full px-3 py-2 border border-gray-300 rounded-md bg-white cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500" onClick={() => setIsOpen(!isOpen)} aria-haspopup="listbox" aria-expanded={isOpen} role="combobox" data-id="element-36">
        {value.length === 0 ? <span className="text-gray-500" data-id="element-37">{placeholder}</span> : <div className="flex flex-wrap gap-1" data-id="element-38">
            {options.filter(option => value.includes(option.value)).map(option => <div key={option.value} className="inline-flex items-center bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-md" data-id="element-39">
                  <span data-id="element-40">{option.label}</span>
                  <button onClick={e => removeOption(option.value, e)} className="ml-1 text-blue-500 hover:text-blue-700 focus:outline-none" aria-label={`Remove ${option.label}`} data-id="element-41">
                    <XIcon className="h-3 w-3" data-id="element-42" />
                  </button>
                </div>)}
          </div>}
        <div className="ml-auto" data-id="element-43">
          <ChevronDownIcon className="h-5 w-5 text-gray-400" data-id="element-44" />
        </div>
      </div>
      {isOpen && <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 overflow-auto focus:outline-none" data-id="element-45">
          <ul role="listbox" aria-multiselectable="true" data-id="element-46">
            {options.map(option => <li key={option.value} role="option" aria-selected={value.includes(option.value)} className={`cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100 ${value.includes(option.value) ? 'bg-blue-50' : ''}`} onClick={() => toggleOption(option.value)} data-id="element-47">
                <div className="flex items-center" data-id="element-48">
                  <span className={`block truncate ${value.includes(option.value) ? 'font-medium' : 'font-normal'}`} data-id="element-49">
                    {option.label}
                  </span>
                </div>
                {value.includes(option.value) && <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600" data-id="element-50">
                    <CheckIcon className="h-4 w-4" data-id="element-51" />
                  </span>}
              </li>)}
          </ul>
        </div>}
    </div>;
};
export default MultiSelect;